import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Phone, 
  MessageCircle, 
  Instagram, 
  Menu, 
  X,
  Award,
  Heart,
  Shield,
  Sparkles,
  Calendar as CalendarIcon,
  Clock,
  Stethoscope,
  Baby,
  MessageSquare,
  Scan,
  GraduationCap,
  Zap,
  CheckCircle2,
  ArrowRight,
  ExternalLink,
  MapPin,
  Image as ImageIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { uk } from "date-fns/locale";

const RAW_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const BACKEND_URL = RAW_BACKEND_URL.trim().replace(/\/$/, "");
const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";
const PUBLIC_URL = process.env.PUBLIC_URL || "";
const SOCIABLEKIT_SCRIPT_SRC = "https://widgets.sociablekit.com/instagram-feed/widget.js";
const SOCIABLEKIT_EMBED_ID = "25679193";

// Doctor photo - professional work photo
const DOCTOR_PHOTO = `${PUBLIC_URL}/gallery/gallery-1.jpg`;

// Contact info
const CONTACT = {
  phone: "+380639768852",
  telegram: "Dr_Nazarova_Valentyna",
  instagram: "dr.valentina_nazarova",
  address: "Одеса, декілька районів міста"
};

// Default gallery images
const DEFAULT_GALLERY = [
  `${PUBLIC_URL}/gallery/gallery-1.jpg`,
  `${PUBLIC_URL}/gallery/gallery-2.jpg`,
  `${PUBLIC_URL}/gallery/gallery-3.jpg`,
  `${PUBLIC_URL}/gallery/gallery-4.jpg`,
  `${PUBLIC_URL}/gallery/gallery-5.jpg`,
  `${PUBLIC_URL}/gallery/gallery-6.jpg`,
  `${PUBLIC_URL}/gallery/gallery-7.jpg`,
];

// Dental SVG Icons (fallback)
const ToothIcon = ({ className, size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2C9.5 2 7.5 3.5 7 6C6.5 8.5 5 10 4 12C3 14 3 16 4 18C5 20 6 22 8 22C10 22 10.5 20 11 18C11.5 16 12 14 12 14C12 14 12.5 16 13 18C13.5 20 14 22 16 22C18 22 19 20 20 18C21 16 21 14 20 12C19 10 17.5 8.5 17 6C16.5 3.5 14.5 2 12 2Z"/>
  </svg>
);

const ImplantIcon = ({ className, size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} stroke="currentColor" strokeWidth="1.5">
    <rect x="9" y="2" width="6" height="4" rx="1"/>
    <path d="M10 6V8M14 6V8"/>
    <path d="M9 8H15L14 12H10L9 8Z"/>
    <path d="M10 12L9 22H15L14 12"/>
    <path d="M9 15H15M9 18H15"/>
  </svg>
);

const SurgeryIcon = ({ className, size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} stroke="currentColor" strokeWidth="1.5">
    <path d="M14 3L21 10L10 21H3V14L14 3Z"/>
    <path d="M7 14L10 17"/>
    <circle cx="18" cy="6" r="2"/>
  </svg>
);

// Services data
const SERVICES = [
  {
    id: "surgery",
    title: "Хірургія",
    icon: SurgeryIcon,
    color: "#6F8C81",
    description: "Операції будь-якої складності: від простого видалення до складних хірургічних втручань з пластикою м'яких тканин.",
    details: ["Атравматичне видалення зубів", "Видалення ретинованих та дистопованих зубів", "Резекція верхівки кореня", "Цистектомія", "Гемісекція"]
  },
  {
    id: "implantation",
    title: "Імплантація",
    icon: ImplantIcon,
    color: "#C47F66",
    description: "Повний цикл імплантологічного лікування: від встановлення імпланту до фіксації формувача ясен.",
    details: ["Дентальна імплантація", "Одномоментна імплантація", "Встановлення формувачів ясен", "Синус-ліфтинг (відкритий/закритий)", "Кісткова пластика"]
  },
  {
    id: "sinus",
    title: "Пластика та синус-ліфтинг",
    icon: Shield,
    color: "#7A9E8E",
    description: "Складні реконструктивні операції: закриття перфорацій верхньощелепних пазух, нарощування кісткової тканини.",
    details: ["Закриття ороантральних сполучень", "Пластика перфорацій пазух", "Аугментація кісткової тканини", "Направлена кісткова регенерація"]
  },
  {
    id: "wisdom",
    title: "Зуби мудрості",
    icon: Sparkles,
    color: "#8BA5A0",
    description: "Видалення третіх молярів (вісімок) будь-якої складності, включаючи повністю ретиновані та горизонтально розташовані.",
    details: ["Прості та складні видалення", "Ретиновані зуби мудрості", "Дистоповані вісімки", "Мінімально інвазивні методики"]
  },
  {
    id: "pediatric",
    title: "Дитяча хірургія",
    icon: Baby,
    color: "#B8A99A",
    description: "Делікатний підхід до маленьких пацієнтів. Безболісні процедури в комфортній атмосфері.",
    details: ["Видалення молочних зубів", "Пластика вуздечки губи", "Пластика вуздечки язика", "Лікування травм"]
  },
  {
    id: "consultation",
    title: "Консультація та діагностика",
    icon: Stethoscope,
    color: "#9AABA6",
    description: "Детальний аналіз КТ-знімків, складання індивідуального плану лікування з узгодженням бюджету.",
    details: ["Аналіз 3D КТ-знімків", "Планування імплантації", "Складання плану лікування", "Друга думка спеціаліста"]
  }
];

// Consultation types
const CONSULTATION_TYPES = [
  { id: "consultation", name: "Консультація" },
  { id: "surgery", name: "Хірургічна операція" },
  { id: "implantation", name: "Імплантація" },
  { id: "pediatric", name: "Дитяча хірургія" },
  { id: "extraction", name: "Видалення зуба" },
  { id: "wisdom-teeth", name: "Видалення зуба мудрості" },
];

// About features
const ABOUT_FEATURES = [
  { icon: GraduationCap, title: "Постійний розвиток", desc: "Регулярне навчання у провідних імплантологів та хірургів" },
  { icon: Award, title: "Складні випадки", desc: "Спеціалізація на нестандартних клінічних ситуаціях" },
  { icon: Scan, title: "3D планування", desc: "Детальний аналіз КТ перед кожною операцією" },
  { icon: Zap, title: "Сучасні протоколи", desc: "Мінімально інвазивні методики лікування" },
];

// Stats
const STATS = [
  { value: "1000+", label: "Видалень на мiсяць" },
  { value: "10+", label: "Років практики" },
  { value: "100%", label: "Прозорість" },
];

const WORKING_HOURS = { start: 9, end: 20 };
const DEFAULT_TIME_SLOTS = Array.from(
  { length: WORKING_HOURS.end - WORKING_HOURS.start + 1 },
  (_, index) => `${String(WORKING_HOURS.start + index).padStart(2, "0")}:00`
);

const toLocalIsoDate = (dateValue) => {
  if (!(dateValue instanceof Date) || Number.isNaN(dateValue.getTime())) return null;
  const year = dateValue.getFullYear();
  const month = String(dateValue.getMonth() + 1).padStart(2, "0");
  const day = String(dateValue.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const mergeTimeSlots = (apiSlots = []) => {
  const slotByTime = new Map(apiSlots.map((slot) => [slot.time, slot]));
  return DEFAULT_TIME_SLOTS.map((time) => slotByTime.get(time) || { time, available: true });
};

const NAME_REGEX = /^[A-Za-zА-Яа-яІіЇїЄєҐґ'’`\-\s]{2,60}$/;
const UA_MOBILE_REGEX = /^(?:\+?380|0)(39|50|63|66|67|68|73|89|91|92|93|94|95|96|97|98|99)\d{7}$/;

const normalizePhone = (value = "") => value.replace(/[\s()-]/g, "");

const LandingPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    consultation_type: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({ name: "", phone: "" });
  const [isMobileCalendar, setIsMobileCalendar] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < 640;
  });
  const [lightboxImage, setLightboxImage] = useState(null);
  const [visibleGallery, setVisibleGallery] = useState(8);
  const [galleryImages] = useState(
    DEFAULT_GALLERY.map((url, i) => ({ id: `d${i}`, url, title: `Робота #${i + 1}` }))
  );

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    fetchSchedule();
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobileCalendar(window.innerWidth < 640);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;

    const existingScript = document.querySelector(`script[src="${SOCIABLEKIT_SCRIPT_SRC}"]`);
    if (existingScript) return;

    const script = document.createElement("script");
    script.src = SOCIABLEKIT_SCRIPT_SRC;
    script.defer = true;
    script.setAttribute("data-sociablekit", "instagram-feed");
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    if (!selectedDate) return;
    const dateStr = toLocalIsoDate(selectedDate);
    const daySchedule = schedule.find(d => d.date === dateStr);
    setAvailableSlots(mergeTimeSlots(daySchedule?.slots || []));
    setSelectedTime(null);
  }, [selectedDate, schedule]);

  const fetchSchedule = async () => {
    try {
      const response = await axios.get(`${API}/schedule?days=30`);
      setSchedule(response.data.schedule);
    } catch (error) {
      console.error("Error fetching schedule:", error);
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) element.scrollIntoView({ behavior: "smooth" });
    setIsMenuOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const normalizedName = formData.name.trim();
    const normalizedPhone = normalizePhone(formData.phone);

    const nextErrors = {
      name: "",
      phone: "",
    };

    if (!normalizedName) {
      nextErrors.name = "Вкажіть, будь ласка, ім'я";
    } else if (!NAME_REGEX.test(normalizedName)) {
      nextErrors.name = "Ім'я має містити 2-60 літер без цифр";
    }

    if (!normalizedPhone) {
      nextErrors.phone = "Вкажіть мобільний номер";
    } else if (!UA_MOBILE_REGEX.test(normalizedPhone)) {
      nextErrors.phone = "Введіть коректний мобільний номер України";
    }

    setFormErrors(nextErrors);

    if (nextErrors.name || nextErrors.phone || !formData.consultation_type) {
      toast.error("Будь ласка, заповніть обов'язкові поля");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        ...formData,
        name: normalizedName,
        phone: normalizedPhone,
        selected_date: toLocalIsoDate(selectedDate),
        selected_time: selectedTime
      };
      await axios.post(`${API}/appointments`, payload);
      toast.success("Дякую, що залишили заявку! Я зв'яжуся з вами найближчим часом і ми уточнимо деталі.");
      setFormData({ name: "", phone: "", consultation_type: "", message: "" });
      setFormErrors({ name: "", phone: "" });
      setSelectedDate(null);
      setSelectedTime(null);
      fetchSchedule();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Помилка при відправці. Спробуйте ще раз.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const isDateDisabled = (date) => {
    const day = date.getDay();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return day === 0 || date < today;
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Decorative dental background elements */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        <div className="absolute top-16 left-[4%] opacity-[0.06] w-24 h-24 rounded-full bg-[#5a9a8a]/20 blur-2xl" />
        <div className="absolute top-[30%] right-[5%] opacity-[0.06] w-20 h-20 rounded-full bg-[#c47f66]/20 blur-2xl" />
        <div className="absolute bottom-[30%] left-[6%] opacity-[0.06] w-24 h-24 rounded-full bg-[#8ba5a0]/20 blur-2xl" />
        <div className="absolute top-[55%] right-[15%] opacity-[0.06] w-20 h-20 rounded-full bg-[#6f8c81]/20 blur-2xl" />
      </div>

      {/* Navigation */}
      <nav 
        data-testid="main-navigation"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? "bg-white/95 backdrop-blur-xl shadow-sm" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <a href="/" data-testid="logo-link" className="flex items-center gap-2.5">
              <ToothIcon size={32} className="text-[#5a9a8a]" />
              <span className="font-['Outfit'] text-lg sm:text-xl font-semibold text-[#2a4a5a]">Dr. Назарова</span>
            </a>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-6">
              {["about", "services", "gallery", "booking"].map((section) => (
                <button 
                  key={section}
                  onClick={() => scrollToSection(section)}
                  className="text-[#5a7a8a] hover:text-[#3d8a7a] text-sm font-medium transition-colors"
                  data-testid={`nav-${section}`}
                >
                  {section === "about" && "Про мене"}
                  {section === "services" && "Послуги"}
                  {section === "gallery" && "Галерея"}
                  {section === "booking" && "Запис"}
                </button>
              ))}
              <Link
                to="/prices"
                className="text-[#4a8a7a] hover:text-white border border-[#9bc9be] hover:bg-[#5a9a8a] rounded-full px-3 py-1.5 text-sm font-semibold transition-colors"
                data-testid="nav-prices-link"
              >
                Ціни в Одесі
              </Link>
              <a 
                href={`https://instagram.com/${CONTACT.instagram}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#5a7a8a] hover:text-[#3d8a7a] text-sm font-medium flex items-center gap-1 transition-colors"
              >
                <Instagram size={16} />
              </a>
              <Button 
                onClick={() => scrollToSection("booking")}
                className="bg-[#5a9a8a] hover:bg-[#4a8a7a] text-white rounded-full px-5 py-2 text-sm"
                data-testid="nav-cta-btn"
              >
                Записатись
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden text-[#1A2421] p-2"
              data-testid="mobile-menu-btn"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-[#E8E6DF]"
            >
              <div className="px-4 py-3 space-y-1">
                {[
                  { id: "about", label: "Про мене" },
                  { id: "services", label: "Послуги" },
                  { id: "gallery", label: "Галерея" },
                  { id: "booking", label: "Запис" },
                ].map((item) => (
                  <button 
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className="block w-full text-left text-[#1A2421] py-3 px-3 rounded-xl hover:bg-[#6F8C81]/5 transition-colors text-sm"
                  >
                    {item.label}
                  </button>
                ))}
                <Link
                  to="/prices"
                  onClick={() => setIsMenuOpen(false)}
                  className="block w-full text-left text-[#4a8a7a] py-3 px-3 rounded-xl border border-[#c5ddd5] hover:bg-[#6F8C81]/5 transition-colors text-sm font-semibold"
                >
                  Ціни та послуги в Одесі
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Hero Section */}
      <section id="hero" data-testid="hero-section" className="min-h-screen pt-16 flex items-center relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-20 w-full">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Text Content - First on mobile */}
            <motion.div 
              className="order-1 text-center lg:text-left"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center justify-center lg:justify-start gap-2 mb-3">
                <p className="text-xs tracking-[0.15em] uppercase font-semibold text-[#5a9a8a]">
                  Хірург-стоматолог
                </p>
              </div>
              <h1 className="font-['Outfit'] text-4xl sm:text-6xl lg:text-7xl font-semibold text-[#2a4a5a] tracking-tight mb-4">
                Валентина
                <br />
                <span className="text-[#5a9a8a]">Назарова</span>
              </h1>
              <p className="text-lg sm:text-xl text-[#6a8a9a] leading-relaxed mb-6 max-w-lg mx-auto lg:mx-0">
                Хірургічна стоматологія повного циклу: від видалення зубів до складних реконструктивних операцій та імплантації
              </p>
              
              {/* Location */}
              <div className="flex items-center justify-center lg:justify-start gap-2 text-sm text-[#6a8a9a] mb-6">
                <MapPin size={16} className="text-[#5a9a8a]" />
                <span>{CONTACT.address}</span>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start w-full sm:w-auto">
                <Button 
                  onClick={() => scrollToSection("booking")}
                  className="bg-[#5a9a8a] hover:bg-[#4a8a7a] text-white rounded-full px-6 py-5 text-sm w-full sm:w-auto"
                  data-testid="hero-cta-button"
                >
                  Записатись на прийом
                  <ArrowRight className="ml-2" size={16} />
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="rounded-full px-6 py-5 text-sm border-[#c5ddd5] text-[#2a4a5a] hover:border-[#5a9a8a] hover:bg-[#f0f8f6] w-full sm:w-auto"
                >
                  <Link to="/prices">Ціни та послуги</Link>
                </Button>
                <a 
                  href={`tel:${CONTACT.phone}`}
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-[#c5ddd5] text-[#2a4a5a] hover:border-[#5a9a8a] hover:bg-[#f0f8f6] transition-all text-sm w-full sm:w-auto"
                  data-testid="hero-phone-btn"
                >
                  <Phone size={16} />
                  <span>{CONTACT.phone}</span>
                </a>
              </div>
            </motion.div>

            {/* Photo - Second on mobile */}
            <motion.div 
              className="order-2"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="relative max-w-[28.8rem] mx-auto lg:max-w-[33.6rem]">
                {/* Gradient background frame */}
                <div className="absolute -inset-3 bg-gradient-to-br from-[#e8f4f0] via-[#f0f8f6] to-[#d5e8e2] rounded-[2rem]"></div>
                <div className="absolute -inset-3 rounded-[2rem] overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-[#5a9a8a]/10 rounded-full blur-2xl"></div>
                  <div className="absolute bottom-0 left-0 w-24 h-24 bg-[#7ab8a8]/20 rounded-full blur-xl"></div>
                </div>
                {/* Decorative tooth icon */}
                <div className="absolute -top-3 -right-3 w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg z-20 border border-[#d5e8e2]">
                  <ToothIcon size={22} className="text-[#5a9a8a]" />
                </div>
                {/* Photo */}
                <img 
                  src={DOCTOR_PHOTO}
                  alt="Доктор Валентина Назарова за роботою"
                  className="relative z-10 w-full object-cover rounded-[1.5rem] shadow-xl"
                  style={{ aspectRatio: '1/1' }}
                  data-testid="hero-doctor-image"
                />
                <div className="absolute -bottom-3 left-4 sm:-bottom-4 sm:left-6 bg-white rounded-xl p-3 shadow-lg flex items-center gap-2 z-20 border border-[#d5e8e2]">
                  <div className="w-8 h-8 rounded-full bg-[#e8f4f0] flex items-center justify-center">
                    <Award className="text-[#5a9a8a]" size={16} />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-[#2a4a5a]">10+ років досвіду</p>
                    <p className="text-[10px] text-[#6a8a9a]">1000+ видалень на мiсяць</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" data-testid="about-section" className="py-16 sm:py-20 lg:py-28 relative z-10 bg-[#f8fbfa]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-10 sm:mb-14"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center justify-center gap-2 mb-3">
              <p className="text-xs tracking-[0.15em] uppercase font-semibold text-[#5a9a8a]">Про мене</p>
            </div>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold text-[#2a4a5a]">
              Мій підхід до лікування
            </h2>
          </motion.div>

          {/* Stats - Compact for mobile */}
          <motion.div 
            className="bg-[#5a9a8a] rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 mb-6 sm:mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="grid grid-cols-3 gap-3 sm:gap-6">
              {STATS.map((stat) => (
                <div key={stat.label} className="text-center">
                  <p className="text-2xl sm:text-4xl lg:text-5xl font-['Outfit'] font-bold text-white">{stat.value}</p>
                  <p className="text-white/80 text-[10px] sm:text-sm mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Content Grid */}
          <div className="grid lg:grid-cols-2 gap-4 sm:gap-6">
            {/* Text Block */}
            <motion.div 
              className="bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-8"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="font-['Outfit'] text-lg sm:text-xl font-semibold text-[#2a4a5a] mb-3 sm:mb-4">
                Прозорість та професійний підхід
              </h3>
              <p className="text-[#6a8a9a] text-sm sm:text-base leading-relaxed mb-3">
                <strong className="text-[#2a4a5a]">До початку лікування</strong> я детально пояснюю план операції, обговорюю всі нюанси, ризики та очікуваний результат. Бюджет узгоджується заздалегідь — ніяких прихованих платежів.
              </p>
              <p className="text-[#6a8a9a] text-sm sm:text-base leading-relaxed">
                Працюю зі складними випадками: ретиновані зуби, перфорації пазух, атрофія кісткової тканини. Кожна операція планується на основі 3D КТ-знімків з урахуванням анатомічних особливостей.
              </p>
            </motion.div>

            {/* Features Grid - Compact */}
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              {ABOUT_FEATURES.map((feature, index) => (
                <motion.div 
                  key={feature.title}
                  className="bg-white rounded-2xl p-4 sm:p-5"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 * index }}
                >
                  <div className="w-10 h-10 rounded-xl bg-[#e8f4f0] flex items-center justify-center mb-3">
                    <feature.icon className="text-[#5a9a8a]" size={20} />
                  </div>
                  <h4 className="font-['Outfit'] text-sm sm:text-base font-semibold text-[#2a4a5a] mb-1">
                    {feature.title}
                  </h4>
                  <p className="text-[#6a8a9a] text-xs sm:text-sm leading-relaxed">{feature.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" data-testid="services-section" className="py-16 sm:py-20 lg:py-28 bg-white relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-10 sm:mb-14"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center justify-center gap-2 mb-3">
              <p className="text-xs tracking-[0.15em] uppercase font-semibold text-[#5a9a8a]">Послуги</p>
            </div>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold text-[#2a4a5a]">
              Чим я можу допомогти
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {SERVICES.map((service, index) => (
              <motion.div 
                key={service.id}
                className="bg-[#f8fbfa] rounded-2xl p-5 hover:shadow-lg transition-shadow border border-[#e0ede8]"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.05 * index }}
                data-testid={`service-${service.id}`}
              >
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
                  style={{ backgroundColor: `${service.color}15` }}
                >
                  <service.icon style={{ color: service.color }} size={20} />
                </div>
                <h3 className="font-['Outfit'] text-base sm:text-lg font-semibold text-[#2a4a5a] mb-2">
                  {service.title}
                </h3>
                <p className="text-[#6a8a9a] text-xs sm:text-sm mb-3 leading-relaxed">
                  {service.description}
                </p>
                <ul className="space-y-1.5">
                  {service.details.slice(0, 4).map((detail, i) => (
                    <li key={i} className="flex items-center gap-2 text-[#2a4a5a] text-xs">
                      <CheckCircle2 className="text-[#5a9a8a] flex-shrink-0" size={12} />
                      <span>{detail}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" data-testid="gallery-section" className="py-16 sm:py-20 lg:py-28 relative z-10 bg-[#f8fbfa]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-10 sm:mb-14"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center justify-center gap-2 mb-3">
              <ImageIcon className="text-[#5a9a8a]" size={20} />
              <p className="text-xs tracking-[0.15em] uppercase font-semibold text-[#5a9a8a]">Галерея</p>
            </div>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold text-[#2a4a5a] mb-3">
              Мої роботи
            </h2>
          </motion.div>

          {/* Gallery Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {galleryImages.slice(0, visibleGallery).map((image, index) => (
              <motion.div 
                key={image.id}
                className="relative rounded-xl sm:rounded-2xl overflow-hidden cursor-pointer group aspect-square"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.05 * (index % 8) }}
                onClick={() => setLightboxImage(image)}
              >
                <img 
                  src={image.url}
                  alt={image.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors" />
              </motion.div>
            ))}
          </div>

          {visibleGallery < galleryImages.length && (
            <div className="text-center mt-8">
              <Button 
                onClick={() => setVisibleGallery(prev => Math.min(prev + 8, galleryImages.length))}
                variant="outline"
                className="rounded-full border-[#c5ddd5] hover:border-[#5a9a8a]"
                data-testid="gallery-load-more"
              >
                Показати більше
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Booking Section */}
      <section id="booking" data-testid="booking-section" className="py-16 sm:py-20 lg:py-28 bg-white relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-10 sm:mb-14"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center justify-center gap-2 mb-3">
              <CalendarIcon className="text-[#5a9a8a]" size={20} />
              <p className="text-xs tracking-[0.15em] uppercase font-semibold text-[#5a9a8a]">Онлайн запис</p>
            </div>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold text-[#2a4a5a] mb-2">
              Записатись на прийом
            </h2>
            <p className="text-[#6a8a9a] text-sm">
              Графік роботи: ПН-СБ з 9:00 до 20:00
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Calendar */}
            <motion.div 
              className="bg-[#f8fbfa] rounded-2xl sm:rounded-3xl p-4 sm:p-6 border border-[#e0ede8]"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center gap-2 mb-4">
                <CalendarIcon className="text-[#5a9a8a]" size={18} />
                <h3 className="font-['Outfit'] text-base sm:text-lg font-semibold text-[#2a4a5a]">
                  Оберіть дату та час
                </h3>
              </div>

              <div className="flex flex-col gap-4">
                {/* Calendar Component */}
                <div data-testid="booking-calendar" className="bg-white rounded-xl p-3 overflow-x-auto">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    disabled={isDateDisabled}
                    locale={uk}
                    numberOfMonths={isMobileCalendar ? 1 : 2}
                    className="mx-auto"
                  />
                </div>

                {/* Time Slots */}
                <div>
                  <p className="text-sm font-medium text-[#2a4a5a] mb-3">
                    {selectedDate 
                      ? `Час на ${selectedDate.toLocaleDateString('uk-UA', { day: 'numeric', month: 'long' })}`
                      : "Оберіть дату"
                    }
                  </p>
                  
                  {selectedDate && (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="time-slots">
                      {availableSlots.length > 0 ? (
                        availableSlots.map((slot) => (
                          <button
                            key={slot.time}
                            onClick={() => slot.available && setSelectedTime(slot.time)}
                            disabled={!slot.available}
                            className={`py-2.5 px-2 rounded-lg text-xs sm:text-sm font-medium border transition-all ${
                              selectedTime === slot.time
                                ? 'bg-[#5a9a8a] text-white border-[#5a9a8a]'
                                : slot.available
                                ? 'bg-white border-[#c5ddd5] text-[#2a4a5a] hover:border-[#5a9a8a]'
                                : 'bg-gray-100 border-gray-200 text-gray-400'
                            }`}
                          >
                            {slot.time}
                          </button>
                        ))
                      ) : (
                        <p className="col-span-4 text-[#6a8a9a] text-sm">Немає доступного часу</p>
                      )}
                    </div>
                  )}

                  {selectedTime && (
                    <div className="mt-4 p-3 bg-[#e8f4f0] rounded-xl">
                      <p className="text-sm text-[#2a4a5a]">
                        <strong>Обрано:</strong> {selectedDate?.toLocaleDateString('uk-UA')} о {selectedTime}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>

            {/* Form */}
            <motion.div 
              className="bg-[#f8fbfa] rounded-2xl sm:rounded-3xl p-4 sm:p-6 border border-[#e0ede8]"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center gap-2 mb-4">
                <MessageSquare className="text-[#5a9a8a]" size={18} />
                <h3 className="font-['Outfit'] text-base sm:text-lg font-semibold text-[#2a4a5a]">
                  Ваші дані
                </h3>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="booking-form">
                <div>
                  <label className="block text-sm font-medium text-[#2a4a5a] mb-1.5">Ваше ім'я *</label>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) => {
                      setFormData({ ...formData, name: e.target.value });
                      if (formErrors.name) setFormErrors((prev) => ({ ...prev, name: "" }));
                    }}
                    placeholder="Введіть ім'я"
                    className="rounded-xl border-[#E8E6DF] bg-white text-sm py-2.5"
                    data-testid="form-name-input"
                    autoComplete="name"
                    required
                  />
                  {formErrors.name && <p className="mt-1 text-xs text-red-600">{formErrors.name}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#2a4a5a] mb-1.5">Телефон *</label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => {
                      setFormData({ ...formData, phone: e.target.value });
                      if (formErrors.phone) setFormErrors((prev) => ({ ...prev, phone: "" }));
                    }}
                    placeholder="+380"
                    className="rounded-xl border-[#E8E6DF] bg-white text-sm py-2.5"
                    data-testid="form-phone-input"
                    inputMode="tel"
                    autoComplete="tel"
                    required
                  />
                  {formErrors.phone && <p className="mt-1 text-xs text-red-600">{formErrors.phone}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#2a4a5a] mb-1.5">Тип консультації *</label>
                  <Select
                    value={formData.consultation_type}
                    onValueChange={(value) => setFormData({ ...formData, consultation_type: value })}
                  >
                    <SelectTrigger className="rounded-xl border-[#c5ddd5] bg-white text-sm py-2.5 h-auto" data-testid="form-consultation-select">
                      <SelectValue placeholder="Оберіть тип" />
                    </SelectTrigger>
                    <SelectContent>
                      {CONSULTATION_TYPES.map((type) => (
                        <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#2a4a5a] mb-1.5">Додаткова інформація</label>
                  <Textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Опишіть проблему"
                    rows={3}
                    className="rounded-xl border-[#E8E6DF] bg-white resize-none text-sm"
                    data-testid="form-message-input"
                  />
                </div>

                <Button 
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#5a9a8a] hover:bg-[#4a8a7a] text-white rounded-xl py-5 text-sm"
                  data-testid="form-submit-btn"
                >
                  {isSubmitting ? "Відправка..." : "Зв'язатись зі мною"}
                </Button>
              </form>

              {/* Additional Contact */}
              <div className="mt-6 pt-6 border-t border-[#e0ede8]">
                <p className="text-sm text-[#6a8a9a] mb-3">Додатково зв'язатися:</p>
                <div className="flex flex-wrap gap-2">
                  <a 
                    href={`tel:${CONTACT.phone}`}
                    className="flex items-center gap-1.5 text-[#2a4a5a] hover:text-[#5a9a8a] text-sm bg-white px-3 py-2 rounded-lg border border-[#e0ede8]"
                  >
                    <Phone size={14} /> Телефон
                  </a>
                  <a 
                    href={`https://t.me/${CONTACT.telegram}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-[#2a4a5a] hover:text-[#5a9a8a] text-sm bg-white px-3 py-2 rounded-lg border border-[#e0ede8]"
                  >
                    <MessageCircle size={14} /> Telegram
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-[#f5efe7] relative overflow-hidden" data-testid="instagram-feed-section">
        <style>{`
          .instagram-feed-grid-4 [class*="instagram-feed"] [class*="items"],
          .instagram-feed-grid-4 [class*="instagram-feed"] [class*="posts"],
          .instagram-feed-grid-4 [class*="instagram-feed"] [class*="gallery"],
          .instagram-feed-grid-4 [class*="instagram-feed"] [class*="grid"] {
            display: grid !important;
            grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
            gap: 12px !important;
          }

          @media (max-width: 1023px) {
            .instagram-feed-grid-4 [class*="instagram-feed"] [class*="items"],
            .instagram-feed-grid-4 [class*="instagram-feed"] [class*="posts"],
            .instagram-feed-grid-4 [class*="instagram-feed"] [class*="gallery"],
            .instagram-feed-grid-4 [class*="instagram-feed"] [class*="grid"] {
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            }
          }

          @media (max-width: 767px) {
            .instagram-feed-grid-4 [class*="instagram-feed"] [class*="items"],
            .instagram-feed-grid-4 [class*="instagram-feed"] [class*="posts"],
            .instagram-feed-grid-4 [class*="instagram-feed"] [class*="gallery"],
            .instagram-feed-grid-4 [class*="instagram-feed"] [class*="grid"] {
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            }
          }
        `}</style>
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 left-0 w-64 h-64 bg-[#d9b38c]/20 blur-3xl rounded-full" />
          <div className="absolute -bottom-24 right-0 w-80 h-80 bg-[#8ab0aa]/20 blur-3xl rounded-full" />
        </div>

        <div className="w-full px-0 sm:px-0 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6 }}
            className="w-full"
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6">
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                  <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#9b6a53]">Instagram</p>
                  <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold text-[#2a4a5a]">Актуальні пости та кейси</h2>
                </div>
                <a
                  href={`https://instagram.com/${CONTACT.instagram}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-[#2a4a5a] hover:text-[#5a9a8a] font-medium"
                >
                  <ExternalLink size={16} /> @{CONTACT.instagram}
                </a>
              </div>
            </div>

            <div className="w-screen relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] px-3 sm:px-6 lg:px-10 instagram-feed-grid-4">
              <div className="rounded-[1.25rem] sm:rounded-[1.5rem] overflow-hidden border border-white/70 bg-white shadow-[0_24px_80px_rgba(42,74,90,0.12)] min-h-[440px] sm:min-h-[620px] p-2 sm:p-3">
                <div className="sk-instagram-feed w-full" data-embed-id={SOCIABLEKIT_EMBED_ID} />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 sm:py-14 bg-[#2a4a5a] relative z-10" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
            <div className="text-center lg:text-left">
              <div className="flex items-center justify-center lg:justify-start gap-2.5 mb-2">
                <ToothIcon size={28} className="text-white" />
                <p className="font-['Outfit'] text-xl sm:text-2xl font-semibold text-white">
                  Dr. Валентина Назарова
                </p>
              </div>
              <p className="text-[#a5c5c0] text-sm">Хірург-стоматолог • {CONTACT.address}</p>
            </div>
            
            <div className="flex flex-wrap items-center justify-center gap-2">
              <a 
                href={`tel:${CONTACT.phone}`}
                className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/10 text-white hover:bg-white/20 text-sm"
              >
                <Phone size={14} /> {CONTACT.phone}
              </a>
              <a 
                href={`https://t.me/${CONTACT.telegram}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/10 text-white hover:bg-white/20 text-sm"
              >
                <MessageCircle size={14} /> Telegram
              </a>
              <a 
                href={`https://instagram.com/${CONTACT.instagram}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/10 text-white hover:bg-white/20 text-sm"
              >
                <Instagram size={14} /> Instagram
              </a>
            </div>
          </div>
          
          <div className="mt-8 pt-6 border-t border-white/10 text-center">
            <p className="text-[#8ab0aa] text-xs">© {new Date().getFullYear()} Dr. Валентина Назарова. Всі права захищені.</p>
          </div>
        </div>
      </footer>

      {/* Lightbox */}
      <Dialog open={!!lightboxImage} onOpenChange={() => setLightboxImage(null)}>
        <DialogContent className="max-w-3xl p-2 bg-black/90 border-0">
          <DialogTitle className="sr-only">Перегляд</DialogTitle>
          {lightboxImage && (
            <img src={lightboxImage.url} alt={lightboxImage.title} className="w-full rounded-lg" />
          )}
        </DialogContent>
      </Dialog>

      {/* Floating CTA (Mobile) */}
      <div className="fixed bottom-4 right-4 lg:hidden z-40">
        <Button 
          onClick={() => scrollToSection("booking")}
          className="bg-[#5a9a8a] hover:bg-[#4a8a7a] text-white rounded-full w-12 h-12 shadow-lg"
          data-testid="floating-cta"
        >
          <CalendarIcon size={20} />
        </Button>
      </div>
    </div>
  );
};

export default LandingPage;
